// src/pages/documents/DocumentCompletionScreen.jsx
import React, { useContext, useState, useEffect, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  Image,
  Alert,
  Platform,
  Modal,
  TextInput,
  ActivityIndicator 
} from "react-native";
import { AppContext } from "../../../App";
import {
  completeVerification,
  fetchQRCode,
  downloadQRCodePDF,
  fetchDriverQRCode,
  generateAndDownloadSticker,
  downloadDoorStickerPDF,
  generateDoorNumber 
} from "../../api/verificationApi";
import { useFormNavigation } from "../../utils/navigationUtils";
import Header from "../../components/Header";
import { useTranslation } from "../../i18n";
import { Buffer } from "buffer";
import * as FileSystem from "expo-file-system";
import * as Sharing from "expo-sharing";
import * as MediaLibrary from "expo-media-library";
import * as IntentLauncher from "expo-intent-launcher";
import QRCode from "qrcode";
import AsyncStorage from '@react-native-async-storage/async-storage';
import generateSummaryPDF  from "../../utils/pdfGenerator";


const COLORS = {
  primary: "#14B8A6",
  secondary: "#7C3AED",
  background: "#F7F9FA",
  card: "#FFFFFF",
  textDark: "#111827",
  textMuted: "#6B7280",
  border: "#E5E7EB",
  success: "#16A34A",
  danger: "#DC2626",
  warning: "#F59E0B",
  successLight: "#DCF7E3",
  warningLight: "#FEF3C7",
  dangerLight: "#FEF2F2",
};

// QR payload builders for driver QR codes (frontend generated)
const makeDriverQrPayload = (personId) =>
  `https://verify.taxi-yaounde.com/driver/${encodeURIComponent(personId)}`;

export default function DocumentCompletionScreen({ navigation }) {
  const { verification, setFormProgress, clearAllData } = useContext(AppContext);
  const { updateCurrentStep } = useFormNavigation();
  const { t, currentLang } = useTranslation();

  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(false);

  const [qrCodeImage, setQrCodeImage] = useState(null);
  const [driverQrImages, setDriverQrImages] = useState([]);
  const [vehicleId, setVehicleId] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingDriverId, setDownloadingDriverId] = useState(null);
  const [surveyModalVisible, setSurveyModalVisible] = useState(false);

  // Preflight modal state
  const [missingModalVisible, setMissingModalVisible] = useState(false);
  // const [documentStatus, setDocumentStatus] = useState({
  //   provided: [],
  //   missing: [],
  //   skipped: []
  // });
  const [documentStatus, setDocumentStatus] = useState({
    missing: [],
    missingCount: 0,
    totalRequired: 0,
    hasMissing: false,
    provided: [],
    skipped: []
  });
  const [vehicleQrBlockedReason, setVehicleQrBlockedReason] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // ✅ Determine if we should show vehicle QR
  const shouldShowVehicleQr = useMemo(() => {
    if (verification?.actorRole === "OWNER") {
      return true;
    }
    if (verification?.actorRole === "DRIVER") {
      return verification?.hasProcuration === true;
    }
    return false;
  }, [verification?.actorRole, verification?.hasProcuration]);

  // ✅ Determine if we should show driver QR
  const shouldShowDriverQr = useMemo(() => {
    if (verification?.actorRole === "OWNER") {
      return (verification?.ownerAddedDriverPersonIds?.length || 0) > 0;
    }
    if (verification?.actorRole === "DRIVER") {
      return !!verification?.driverPersonId;
    }
    return false;
  }, [
    verification?.actorRole, 
    verification?.ownerAddedDriverPersonIds, 
    verification?.driverPersonId
  ]);

  useEffect(() => {
    updateCurrentStep("document_upload_completion_screen");
  }, []);

  // --- helper: arraybuffer -> base64 (only for vehicle QR)
  const arrayBufferToBase64 = (data) => {
    const buf = data instanceof ArrayBuffer ? Buffer.from(new Uint8Array(data)) : Buffer.from(data);
    return buf.toString("base64");
  };

  // ✅ Comprehensive document status tracking
// Add this function to your DocumentCompletionScreen.jsx
// Tracks document status using ONLY your existing verification state structure

function getDocumentStatus(v) {
  const docs = v?.documents || {};
  const missing = [];
  const provided = [];
  const skipped = [];

  // Document definitions - using ONLY your existing fields
  const documentDefinitions = [
  {
    key: 'carteGrise',
    label: 'Carte grise',
    skipFlag: 'carRegistrationSkipped',
    icon: '📄',
  },
  {
    key: 'carteTransport',
    label: 'Carte de transport public',
    skipFlag: 'publicTransportSkipped',
    icon: '🚌',
  },
  {
    key: 'recuImpots',
    label: "Impôt Général Synthétique (IGS)",
    skipFlag: 'taxReceiptSkipped',
    icon: '💰',
  },
  {
    key: 'licenceTransport',
    label: 'Licence de transport',
    skipFlag: 'transportLicenceSkipped',
    icon: '📋',
  },
  {
    key: 'technicalInspection',
    label: 'Visite technique taxi',
    skipFlag: 'technicalInspectionSkipped',
    icon: '🔧',
  },
  {
    key: 'insurance',
    label: 'Assurance quatrième catégorie A',
    skipFlag: 'insuranceSkipped',
    icon: '🛡️',
  },
];

function documentHasFiles(value) {
  if (Array.isArray(value)) return value.length > 0;

  if (value && typeof value === 'object') {
    return (
      (Array.isArray(value.pdf) && value.pdf.length > 0) ||
      (
        Array.isArray(value.front) &&
        value.front.length > 0 &&
        Array.isArray(value.back) &&
        value.back.length > 0
      )
    );
  }

  return false;
}

  // Check each document type
  documentDefinitions.forEach(doc => {
    const isSkipped = Boolean(docs?.[doc.skipFlag]);
    const files = docs?.[doc.key] || null;
    const hasFiles = documentHasFiles(files);

    if (isSkipped) {
      // Document was explicitly skipped
      skipped.push({
        ...doc,
        status: 'skipped',
        message: 'Document ignoré',
        fileCount: 0
      });
    } else if (hasFiles) {
      // Document has files uploaded
      provided.push({
        ...doc,
        status: 'provided',
        fileCount: Array.isArray(files)
          ? files.length
          : (files?.pdf?.length || 0) + (files?.front?.length || 0) + (files?.back?.length || 0),
        message: 'Document fourni'
      });
    } else {
      // Document is missing (not skipped, no files)
      missing.push({
        ...doc,
        status: 'missing',
        message: 'Document requis non fourni',
        fileCount: 0
      });
    }
  });

  // Check procuration for driver workflow - using your existing fields
  if (v?.actorRole === "DRIVER") {
    const hasProc = Boolean(v?.hasProcuration);
    const isSkipped = Boolean(v?.owner?.skipProcuration);
    
    const procurationDoc = {
      key: "procuration",
      label: "Procuration",
      icon: "📝"
    };

    if (isSkipped) {
      skipped.push({
        ...procurationDoc,
        status: 'skipped',
        message: 'Procuration ignorée',
        fileCount: 0
      });
    } else if (hasProc) {
      provided.push({
        ...procurationDoc,
        status: 'provided',
        fileCount: 1,
        message: 'Procuration fournie'
      });
    } else {
      missing.push({
        ...procurationDoc,
        status: 'missing',
        message: 'Procuration requise pour QR véhicule',
        fileCount: 0
      });
    }
  }

  // Return ONLY missing and total counts - this is what you asked for
  return {
    missing,           // Array of missing documents
    missingCount: missing.length,
    totalRequired: documentDefinitions.length + (v?.actorRole === "DRIVER" ? 1 : 0),
    hasMissing: missing.length > 0,
    
    // Also return these for the modal to show full status
    provided,
    skipped,
    providedCount: provided.length,
    skippedCount: skipped.length
  };
}

  // const handleFinishPress = () => {
  //   console.log("handleFinishPress called");
    
  //   const status = getDocumentStatus(verification);
  //   console.log("Document status:", status);
    
  //   setDocumentStatus(status);

  //   // If driver has no procuration, show blocking message for VEHICLE QR
  //   if (verification?.actorRole === "DRIVER" && !verification?.hasProcuration) {
  //     console.log("Driver workflow without procuration - vehicle QR blocked");
  //     setVehicleQrBlockedReason(
  //       "Le QR code du véhicule ne peut pas être généré tant que la procuration n'est pas fournie."
  //     );
  //   } else {
  //     setVehicleQrBlockedReason(null);
  //   }

  //   console.log("Setting modal visible to true");
  //   setMissingModalVisible(true);
  // };

  const handleFinishPress = () => {
    console.log("handleFinishPress called");
    
    // Get document status using ONLY your existing fields
    const status = getDocumentStatus(verification);
    console.log("Missing documents:", status.missing);
    console.log("Missing count:", status.missingCount);
    
    setDocumentStatus(status);

    // If driver has no procuration, show blocking message for VEHICLE QR
    if (verification?.actorRole === "DRIVER" && !verification?.hasProcuration) {
      console.log("Driver workflow without procuration - vehicle QR blocked");
     setVehicleQrBlockedReason(
  t("completion.vehicleQrBlockedReason")
);

    } else {
      setVehicleQrBlockedReason(null);
    }

    setMissingModalVisible(true);
  };

  const driverPersonIds = useMemo(() => {
    if (!verification) return [];
    return verification.actorRole === "OWNER"
      ? (verification.ownerAddedDriverPersonIds || [])
      : (verification.driverPersonId ? [verification.driverPersonId] : []);
  }, [verification]);

  // const proceedFinish = async () => {
  //   setMissingModalVisible(false);

  //   try {
  //     setSaving(true);

  //     const payload = {
  //       actorRole: verification.actorRole,
  //       ownerAddedDriverPersonIds: verification.ownerAddedDriverPersonIds || [],
  //       driverPersonId: verification.driverPersonId || null,
  //     };

  //     const response = await completeVerification(verification.verificationId, payload);

  //     setVehicleId(response.vehicleId || null);

  //     const serverBlockedReason = response?.blocked?.vehicleQrReason || null;
  //     if (serverBlockedReason) setVehicleQrBlockedReason(serverBlockedReason);
  //     else setVehicleQrBlockedReason(null);

  //     const vehicleQrGenerated = Boolean(response?.generated?.vehicleQr);
      
  //     if (shouldShowVehicleQr && vehicleQrGenerated && response?.vehicleId) {
  //       try {
  //         const qrCodeData = await fetchQRCode(response.vehicleId);
  //         const base64String = arrayBufferToBase64(qrCodeData);
          
  //         if (Platform.OS === "web") {
  //           setQrCodeImage(`data:image/png;base64,${base64String}`);
  //         } else {
  //           const path = `${FileSystem.cacheDirectory}vehicle_qr_${response.vehicleId}.png`;
  //           await FileSystem.writeAsStringAsync(path, base64String, { 
  //             encoding: FileSystem.EncodingType.Base64 
  //           });
  //           setQrCodeImage(path);
  //         }
  //       } catch (qrError) {
  //         console.warn("Could not fetch vehicle QR code:", qrError);
  //         setQrCodeImage(null);
  //       }
  //     } else {
  //       setQrCodeImage(null);
  //     }

  //     if (shouldShowDriverQr) {
  //       const rawDriverItems = Array.isArray(response?.generated?.driverQrs)
  //         ? response.generated.driverQrs
  //         : [];

  //       const driverPersonIds = rawDriverItems
  //         .map((item) => (typeof item === "object" ? item?.personId : item))
  //         .filter(Boolean);

  //       const driverImgs = [];

  //       for (const pid of driverPersonIds) {
  //         try {
  //           const payload = makeDriverQrPayload(pid);
  //           const dataUrl = await QRCode.toDataURL(payload, {
  //             errorCorrectionLevel: "M",
  //             margin: 1,
  //             scale: 8,
  //             width: 300,
  //           });
            
  //           let filePath = null;
  //           if (Platform.OS !== "web") {
  //             const base64Data = dataUrl.split(',')[1];
  //             filePath = `${FileSystem.cacheDirectory}driver_qr_${pid}.png`;
  //             await FileSystem.writeAsStringAsync(filePath, base64Data, {
  //               encoding: FileSystem.EncodingType.Base64,
  //             });
  //           }
            
  //           driverImgs.push({ 
  //             personId: pid, 
  //             uri: dataUrl,
  //             filePath
  //           });
  //         } catch (e) {
  //           console.warn("Could not generate driver QR for personId:", pid, e);
  //         }
  //       }

  //       setDriverQrImages(driverImgs);
  //     } else {
  //       setDriverQrImages([]);
  //     }

  //     setCompleted(true);

  //     setFormProgress((prev) => ({
  //       ...prev,
  //       completedSteps: [
  //         ...new Set([...prev.completedSteps, "document_upload_completion_screen"]),
  //       ],
  //       currentStep: "document_upload_completion_screen",
  //     }));
  //   } catch (e) {
  //     console.log("Error completing verification:", e);
  //     Alert.alert(t("common.error"), t("completion.completeError"), [
  //       { text: t("common.ok") },
  //     ]);
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  // const handleDownloadVehicleQR = async () => {
  //   if (!vehicleId) return;

  //   try {
  //     setDownloading(true);
  //     const pdfArrayBuffer = await downloadQRCodePDF(vehicleId);

  //     if (Platform.OS === "web") {
  //       const blob = new Blob([pdfArrayBuffer], { type: "application/pdf" });
  //       const url = window.URL.createObjectURL(blob);
  //       const link = document.createElement("a");
  //       link.href = url;
  //       link.download = `qr_code_vehicule_${vehicleId}.pdf`;
  //       document.body.appendChild(link);
  //       link.click();
  //       document.body.removeChild(link);
  //       window.URL.revokeObjectURL(url);
  //     } else {
  //       const b64 = Buffer.from(new Uint8Array(pdfArrayBuffer)).toString("base64");
  //       const path = `${FileSystem.cacheDirectory}qr_code_vehicule_${vehicleId}.pdf`;

  //       await FileSystem.writeAsStringAsync(path, b64, {
  //         encoding: FileSystem.EncodingType.Base64,
  //       });

  //       await Sharing.shareAsync(path);
  //     }
  //   } catch (e) {
  //     console.error("Download error:", e);
  //     Alert.alert(t("common.error"), t("completion.downloadError"), [{ text: t("common.ok") }]);
  //   } finally {
  //     setDownloading(false);
  //   }
  // };


const proceedFinish = async () => {
  setMissingModalVisible(false);

  try {
    setSaving(true);

    const payload = {
      actorRole: verification.actorRole,
      ownerAddedDriverPersonIds: verification.ownerAddedDriverPersonIds || [],
      driverPersonId: verification.driverPersonId || null,
    };

    // Pass option to generate door number automatically
    const response = await completeVerification(
      verification.verificationId, 
      payload,
      { generateDoorNumber: true } // This will trigger door number generation
    );

    const completedVehicleId = response.vehicleId || verification.vehicleId || null;
    setVehicleId(completedVehicleId);
    
    // Store door number info if available
    if (response.doorNumber) {
      console.log('Door number generated:', response.doorNumber);
      // You can store this in state if you want to display it
      // setDoorNumber(response.doorNumber);
    }

    setVehicleQrBlockedReason(
      verification.actorRole === "DRIVER" && !verification.hasProcuration
        ? t("completion.vehicleQrBlockedReason")
        : null
    );

    if (shouldShowVehicleQr && completedVehicleId && verification.hasProcuration !== false) {
      try {
        const qrCodeData = await fetchQRCode(completedVehicleId);
        const base64String = arrayBufferToBase64(qrCodeData);

        if (Platform.OS === "web") {
          setQrCodeImage(`data:image/png;base64,${base64String}`);
        } else {
          const path = `${FileSystem.cacheDirectory}vehicle_qr_${completedVehicleId}.png`;
          await FileSystem.writeAsStringAsync(path, base64String, {
            encoding: FileSystem.EncodingType.Base64,
          });
          setQrCodeImage(path);
        }
      } catch (qrError) {
        console.warn("Could not fetch vehicle QR code:", qrError);
        setQrCodeImage(null);
      }
    } else {
      setQrCodeImage(null);
    }

    if (shouldShowDriverQr) {
      const driverImages = [];

      for (const personId of driverPersonIds) {
        try {
          const qrData = await fetchDriverQRCode(personId);
          const base64String = arrayBufferToBase64(qrData);
          const dataUrl = `data:image/png;base64,${base64String}`;
          let filePath = null;

          if (Platform.OS !== "web") {
            filePath = `${FileSystem.cacheDirectory}driver_qr_${personId}.png`;
            await FileSystem.writeAsStringAsync(filePath, base64String, {
              encoding: FileSystem.EncodingType.Base64,
            });
          }

          driverImages.push({ personId, uri: dataUrl, filePath });
        } catch (qrError) {
          console.warn("Could not fetch driver QR code:", qrError);
        }
      }

      setDriverQrImages(driverImages);
    } else {
      setDriverQrImages([]);
    }

    setCompleted(true);

    setFormProgress((prev) => ({
      ...prev,
      completedSteps: [
        ...new Set([...prev.completedSteps, "document_upload_completion_screen"]),
      ],
      currentStep: "document_upload_completion_screen",
    }));
  } catch (e) {
    console.log("Error completing verification:", e);
    Alert.alert(t("common.error"), t("completion.completeError"), [
      { text: t("common.ok") },
    ]);
  } finally {
    setSaving(false);
  }
};

const handleDownloadVehicleQR = async () => {
  if (!vehicleId) return;

  try {
    setDownloading(true);
    
    // Option 1: Generate door number and download sticker in one go
    const result = await generateAndDownloadSticker(vehicleId, 'standard');
    
    // The result contains:
    // - result.doorNumber: The door number (e.g., "05 321 CUY")
    // - result.alreadyExists: Boolean indicating if door number already existed
    // - result.sequenceNumber: The sequence number
    // - result.pdfData: The PDF data (arraybuffer)
    
    const pdfArrayBuffer = result.pdfData;
    const doorNumber = result.doorNumber;
    
    // Optional: Show a message to the user
    if (result.alreadyExists) {
      console.log('Using existing door number:', doorNumber);
    } else {
      console.log('Generated new door number:', doorNumber);
    }

    // Download the PDF
    if (Platform.OS === "web") {
      const blob = new Blob([pdfArrayBuffer], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `sticker_portiere_${doorNumber.replace(/\s/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else {
      const b64 = Buffer.from(new Uint8Array(pdfArrayBuffer)).toString("base64");
      const path = `${FileSystem.cacheDirectory}sticker_portiere_${doorNumber.replace(/\s/g, '_')}.pdf`;

      await FileSystem.writeAsStringAsync(path, b64, {
        encoding: FileSystem.EncodingType.Base64,
      });

      await Sharing.shareAsync(path);
    }
  } catch (e) {
    console.error("Download error:", e);
    Alert.alert(t("common.error"), t("completion.downloadError"), [{ text: t("common.ok") }]);
  } finally {
    setDownloading(false);
  }
};

  const handleDownloadDriverQR = async (driver) => {
    if (!driver) return;

    try {
      setDownloadingDriverId(driver.personId);

      if (Platform.OS === "web") {
        const link = document.createElement("a");
        link.href = driver.uri;
        link.download = `qr_code_chauffeur_${driver.personId}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        let filePath = driver.filePath;
        
        if (!filePath) {
          const base64Data = driver.uri.split(',')[1];
          filePath = `${FileSystem.cacheDirectory}driver_qr_${driver.personId}.png`;
          await FileSystem.writeAsStringAsync(filePath, base64Data, {
            encoding: FileSystem.EncodingType.Base64,
          });
        }

        const fileInfo = await FileSystem.getInfoAsync(filePath);
        if (fileInfo.exists) {
          await Sharing.shareAsync(filePath);
        } else {
          Alert.alert(t("common.error"), "Le fichier QR code n'a pas pu être trouvé.");
        }
      }
    } catch (e) {
      console.error("Download driver QR error:", e);
      Alert.alert(t("common.error"), t("completion.downloadError"), [{ text: t("common.ok") }]);
    } finally {
      setDownloadingDriverId(null);
    }
  };



const gatherSummaryData = () => {
  return {
    verification, // This now contains ALL the data from our updated structure
    documentStatus: getDocumentStatus(verification),
    submissionDate: new Date().toISOString(),
    vehicleQrGenerated: !!qrCodeImage,
    driverQrsGenerated: driverQrImages.length
  };
};

// Add this function to handle PDF generation
// const handleDownloadSummary = async () => {
//   try {
//     setGeneratingPDF(true);
    
//     const summaryData = gatherSummaryData();
//     const result = await generateSummaryPDF(summaryData);
    
//     if (result.success) {
//       Alert.alert(
//         t('common.success'),
//         t('completion.pdfGenerated', 'Le récapitulatif a été généré avec succès')
//       );
//     } else {
//       throw new Error('PDF generation failed');
//     }
//   } catch (error) {
//     console.error('Error generating summary:', error);
//     Alert.alert(
//       t('common.error'),
//       t('completion.pdfError', 'Erreur lors de la génération du PDF')
//     );
//   } finally {
//     setGeneratingPDF(false);
//   }
// };

// const { t, language } = useTranslation();

// In DocumentCompletionScreen.jsx, update the handleDownloadSummary function

const handleDownloadSummary = async () => {
  try {
    setGeneratingPDF(true);
    
    const summaryData = gatherSummaryData();
    console.log('Summary data being sent to PDF generator:', currentLang, summaryData);
    // Pass the current language from your translation hook
    const result = await generateSummaryPDF(summaryData, currentLang);
    
    if (result.success) {
      Alert.alert(
        t('common.success'),
        t('completion.pdfGenerated')
      );
    } else {
      throw new Error('PDF generation failed');
    }
  } catch (error) {
    console.error('Error generating summary:', error);
    Alert.alert(
      t('common.error'),
      t('completion.pdfError')
    );
  } finally {
    setGeneratingPDF(false);
  }
};

  // const handleStartNew = () => {
  //   clearAllData();
  //   navigation.navigate("ownership_screen");
  // };
  // const handleStartNew = async () => {
  // // Check if it's the user's first time completing a form
  //   const hasCompletedFormBefore = await AsyncStorage.getItem('hasCompletedForm');
    
  //   if (!hasCompletedFormBefore) {
  //     // First time - show survey
  //     setSurveyModalVisible(true);
  //   } else {
  //     // Not first time - show survey only occasionally (20% chance)
  //     const shouldShowSurvey = Math.random() < 0.2;
  //     if (shouldShowSurvey) {
  //       setSurveyModalVisible(true);
  //     } else {
  //       // Clear data and navigate directly
  //       clearAllData();
  //       navigation.navigate("ownership_screen");
  //     }
  //   }
  // };

  const handleStartNew = async () => {
  // Simply clear data and navigate - survey is now separate
  clearAllData();
  navigation.navigate("ownership_screen");
};

  const handleSurveyComplete = async () => {
    setSurveyModalVisible(false);
    
    // Mark that user has completed a form
    await AsyncStorage.setItem('hasCompletedForm', 'true');
    
    // Also store timestamp of last survey
    await AsyncStorage.setItem('lastSurveyTimestamp', Date.now().toString());
    
    // Clear data and navigate
    clearAllData();
    navigation.navigate("ownership_screen");
  };

 // ✅ Enhanced modal with document status tracking
// const MissingDocsModal = () => {
//   console.log("MissingDocsModal rendering, visible:", missingModalVisible);

//   return (
//     <Modal
//       visible={missingModalVisible}
//       transparent
//       animationType="fade"
//       onRequestClose={() => setMissingModalVisible(false)}
//     >
//       <View style={styles.modalOverlay}>
//         <View style={styles.modalCard}>
//           <Text style={styles.modalTitle}>{t("completion.docsStatusTitle")}</Text>

//           {/* Document Status List - Clean and Unified */}
//           <View style={styles.documentListContainer}>
//             {/* All documents in one unified list */}
//             <View style={styles.documentList}>
//               {/* Carte Grise */}
//               <View style={styles.documentListItem}>
//                 <View style={styles.documentIconContainer}>
//                   <Text style={styles.documentIcon}>📄</Text>
//                 </View>
//                 <View style={styles.documentContent}>
//                   <Text style={styles.documentName}>
//                     {t("completion.docs.carRegistration")}
//                   </Text>
//                   <Text style={styles.documentSubtext}>
//                     {verification?.documents?.carRegistrationSkipped
//                       ? t("completion.docs.status.ignored")
//                       : verification?.documents?.carteGrise?.length > 0
//                       ? t("completion.docs.status.filesCount").replace(
//                           "{{count}}",
//                           verification.documents.carteGrise.length
//                         )
//                       : t("completion.docs.status.notProvided")}
//                   </Text>
//                 </View>
//                 <View style={styles.documentStatus}>
//                   <Text
//                     style={[
//                       styles.statusIcon,
//                       verification?.documents?.carRegistrationSkipped ||
//                       verification?.documents?.carteGrise?.length === 0
//                         ? styles.statusMissing
//                         : styles.statusSuccess,
//                     ]}
//                   >
//                     {verification?.documents?.carRegistrationSkipped ||
//                     verification?.documents?.carteGrise?.length === 0
//                       ? "❌"
//                       : "✅"}
//                   </Text>
//                 </View>
//               </View>

//               {/* Carte de Transport */}
//               <View style={styles.documentListItem}>
//                 <View style={styles.documentIconContainer}>
//                   <Text style={styles.documentIcon}>🚌</Text>
//                 </View>
//                 <View style={styles.documentContent}>
//                   <Text style={styles.documentName}>
//                     {t("completion.docs.transportCard")}
//                   </Text>
//                   <Text style={styles.documentSubtext}>
//                     {verification?.documents?.publicTransportSkipped
//                       ? t("completion.docs.status.ignored")
//                       : verification?.documents?.carteTransport?.length > 0
//                       ? t("completion.docs.status.filesCount").replace(
//                           "{{count}}",
//                           verification.documents.carteTransport.length
//                         )
//                       : t("completion.docs.status.notProvided")}
//                   </Text>
//                 </View>
//                 <View style={styles.documentStatus}>
//                   <Text
//                     style={[
//                       styles.statusIcon,
//                       verification?.documents?.publicTransportSkipped ||
//                       verification?.documents?.carteTransport?.length === 0
//                         ? styles.statusMissing
//                         : styles.statusSuccess,
//                     ]}
//                   >
//                     {verification?.documents?.publicTransportSkipped ||
//                     verification?.documents?.carteTransport?.length === 0
//                       ? "❌"
//                       : "✅"}
//                   </Text>
//                 </View>
//               </View>

//               {/* Reçu d'Impôts */}
//               <View style={styles.documentListItem}>
//                 <View style={styles.documentIconContainer}>
//                   <Text style={styles.documentIcon}>💰</Text>
//                 </View>
//                 <View style={styles.documentContent}>
//                   <Text style={styles.documentName}>
//                     {t("completion.docs.taxReceipt")}
//                   </Text>
//                   <Text style={styles.documentSubtext}>
//                     {verification?.documents?.taxReceiptSkipped
//                       ? t("completion.docs.status.ignored")
//                       : verification?.documents?.recuImpots?.length > 0
//                       ? t("completion.docs.status.filesCount").replace(
//                           "{{count}}",
//                           verification.documents.recuImpots.length
//                         )
//                       : t("completion.docs.status.notProvided")}
//                   </Text>
//                 </View>
//                 <View style={styles.documentStatus}>
//                   <Text
//                     style={[
//                       styles.statusIcon,
//                       verification?.documents?.taxReceiptSkipped ||
//                       verification?.documents?.recuImpots?.length === 0
//                         ? styles.statusMissing
//                         : styles.statusSuccess,
//                     ]}
//                   >
//                     {verification?.documents?.taxReceiptSkipped ||
//                     verification?.documents?.recuImpots?.length === 0
//                       ? "❌"
//                       : "✅"}
//                   </Text>
//                 </View>
//               </View>

//               {/* Licence de Transport */}
//               <View style={styles.documentListItem}>
//                 <View style={styles.documentIconContainer}>
//                   <Text style={styles.documentIcon}>📋</Text>
//                 </View>
//                 <View style={styles.documentContent}>
//                   <Text style={styles.documentName}>
//                     {t("completion.docs.transportLicence")}
//                   </Text>
//                   <Text style={styles.documentSubtext}>
//                     {verification?.documents?.transportLicenceSkipped
//                       ? t("completion.docs.status.ignored")
//                       : verification?.documents?.licenceTransport?.length > 0
//                       ? t("completion.docs.status.filesCount").replace(
//                           "{{count}}",
//                           verification.documents.licenceTransport.length
//                         )
//                       : t("completion.docs.status.notProvided")}
//                   </Text>
//                 </View>
//                 <View style={styles.documentStatus}>
//                   <Text
//                     style={[
//                       styles.statusIcon,
//                       verification?.documents?.transportLicenceSkipped ||
//                       verification?.documents?.licenceTransport?.length === 0
//                         ? styles.statusMissing
//                         : styles.statusSuccess,
//                     ]}
//                   >
//                     {verification?.documents?.transportLicenceSkipped ||
//                     verification?.documents?.licenceTransport?.length === 0
//                       ? "❌"
//                       : "✅"}
//                   </Text>
//                 </View>
//               </View>

//               {/* Procuration - Only for DRIVER workflow */}
//               {verification?.actorRole === "DRIVER" && (
//                 <View style={styles.documentListItem}>
//                   <View style={styles.documentIconContainer}>
//                     <Text style={styles.documentIcon}>📝</Text>
//                   </View>
//                   <View style={styles.documentContent}>
//                     <Text style={styles.documentName}>
//                       {t("completion.docs.procuration")}
//                     </Text>
//                     <Text style={styles.documentSubtext}>
//                       {verification?.owner?.skipProcuration
//                         ? t("completion.docs.status.ignored")
//                         : verification?.hasProcuration
//                         ? t("completion.docs.status.provided")
//                         : t("completion.docs.status.notProvidedFemale")}
//                     </Text>
//                   </View>
//                   <View style={styles.documentStatus}>
//                     <Text
//                       style={[
//                         styles.statusIcon,
//                         verification?.owner?.skipProcuration ||
//                         !verification?.hasProcuration
//                           ? styles.statusMissing
//                           : styles.statusSuccess,
//                       ]}
//                     >
//                       {verification?.owner?.skipProcuration ||
//                       !verification?.hasProcuration
//                         ? "❌"
//                         : "✅"}
//                     </Text>
//                   </View>
//                 </View>
//               )}
//             </View>

//             {/* Procuration blocking message for vehicle QR - only if needed */}
//             {!!vehicleQrBlockedReason && (
//               <View style={styles.blockBox}>
//                 <Text style={styles.blockTitle}>
//                   {t("completion.docs.blockTitle")}
//                 </Text>
//                 <Text style={styles.blockText}>{vehicleQrBlockedReason}</Text>
//               </View>
//             )}

//             {/* Action Buttons */}
//             <View style={styles.modalButtonsRow}>
//               <Pressable
//                 style={({ pressed }) => [
//                   styles.modalSecondaryBtn,
//                   pressed && { opacity: 0.9 },
//                 ]}
//                 onPress={() => setMissingModalVisible(false)}
//                 disabled={saving}
//               >
//                 <Text style={styles.modalSecondaryText}>
//                   {t("completion.docs.back")}
//                 </Text>
//               </Pressable>

//               <Pressable
//                 style={({ pressed }) => [
//                   styles.modalPrimaryBtn,
//                   pressed && { opacity: 0.9 },
//                   saving && { opacity: 0.7 },
//                 ]}
//                 onPress={proceedFinish}
//                 disabled={saving}
//               >
//                 {saving ? (
//                   <ActivityIndicator color="#FFFFFF" />
//                 ) : (
//                   <Text style={styles.modalPrimaryText}>
//                     {t("completion.docs.continue")}
//                   </Text>
//                 )}
//               </Pressable>
//             </View>
//           </View>
//         </View>
//       </View>
//     </Modal>
//   );
// };
const MissingDocsModal = () => {
  console.log("MissingDocsModal rendering, visible:", missingModalVisible);

  return (
    <Modal
      visible={missingModalVisible}
      transparent
      animationType="fade"
      onRequestClose={() => setMissingModalVisible(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalCard}>
          {/* Fixed Header - stays at top */}
          <Text style={styles.modalTitle}>{t("completion.docsStatusTitle")}</Text>

          {/* Scrollable Content */}
          <ScrollView 
            style={styles.modalScrollView}
            contentContainerStyle={styles.modalScrollContent}
            showsVerticalScrollIndicator={true}
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.documentListContainer}>
              {/* All documents in one unified list */}
              <View style={styles.documentList}>
                {/* Carte Grise */}
                <View style={styles.documentListItem}>
                  <View style={styles.documentIconContainer}>
                    <Text style={styles.documentIcon}>📄</Text>
                  </View>
                  <View style={styles.documentContent}>
                    <Text style={styles.documentName}>
                      {t("completion.docs.carRegistration")}
                    </Text>
                    <Text style={styles.documentSubtext}>
                      {verification?.documents?.carRegistrationSkipped
                        ? t("completion.docs.status.ignored")
                        : verification?.documents?.carteGrise?.length > 0
                        ? t("completion.docs.status.filesCount").replace(
                            "{{count}}",
                            verification.documents.carteGrise.length
                          )
                        : t("completion.docs.status.notProvided")}
                    </Text>
                  </View>
                  <View style={styles.documentStatus}>
                    <Text
                      style={[
                        styles.statusIcon,
                        verification?.documents?.carRegistrationSkipped ||
                        verification?.documents?.carteGrise?.length === 0
                          ? styles.statusMissing
                          : styles.statusSuccess,
                      ]}
                    >
                      {verification?.documents?.carRegistrationSkipped ||
                      verification?.documents?.carteGrise?.length === 0
                        ? "❌"
                        : "✅"}
                    </Text>
                  </View>
                </View>

                {/* Carte de Transport */}
                <View style={styles.documentListItem}>
                  <View style={styles.documentIconContainer}>
                    <Text style={styles.documentIcon}>🚌</Text>
                  </View>
                  <View style={styles.documentContent}>
                    <Text style={styles.documentName}>
                      {t("completion.docs.transportCard")}
                    </Text>
                    <Text style={styles.documentSubtext}>
                      {verification?.documents?.publicTransportSkipped
                        ? t("completion.docs.status.ignored")
                        : verification?.documents?.carteTransport?.length > 0
                        ? t("completion.docs.status.filesCount").replace(
                            "{{count}}",
                            verification.documents.carteTransport.length
                          )
                        : t("completion.docs.status.notProvided")}
                    </Text>
                  </View>
                  <View style={styles.documentStatus}>
                    <Text
                      style={[
                        styles.statusIcon,
                        verification?.documents?.publicTransportSkipped ||
                        verification?.documents?.carteTransport?.length === 0
                          ? styles.statusMissing
                          : styles.statusSuccess,
                      ]}
                    >
                      {verification?.documents?.publicTransportSkipped ||
                      verification?.documents?.carteTransport?.length === 0
                        ? "❌"
                        : "✅"}
                    </Text>
                  </View>
                </View>

                {/* Reçu d'Impôts */}
                <View style={styles.documentListItem}>
                  <View style={styles.documentIconContainer}>
                    <Text style={styles.documentIcon}>💰</Text>
                  </View>
                  <View style={styles.documentContent}>
                    <Text style={styles.documentName}>
                      {t("completion.docs.taxReceipt")}
                    </Text>
                    <Text style={styles.documentSubtext}>
                      {verification?.documents?.taxReceiptSkipped
                        ? t("completion.docs.status.ignored")
                        : verification?.documents?.recuImpots?.length > 0
                        ? t("completion.docs.status.filesCount").replace(
                            "{{count}}",
                            verification.documents.recuImpots.length
                          )
                        : t("completion.docs.status.notProvided")}
                    </Text>
                  </View>
                  <View style={styles.documentStatus}>
                    <Text
                      style={[
                        styles.statusIcon,
                        verification?.documents?.taxReceiptSkipped ||
                        verification?.documents?.recuImpots?.length === 0
                          ? styles.statusMissing
                          : styles.statusSuccess,
                      ]}
                    >
                      {verification?.documents?.taxReceiptSkipped ||
                      verification?.documents?.recuImpots?.length === 0
                        ? "❌"
                        : "✅"}
                    </Text>
                  </View>
                </View>

                {/* Licence de Transport */}
                <View style={styles.documentListItem}>
                  <View style={styles.documentIconContainer}>
                    <Text style={styles.documentIcon}>📋</Text>
                  </View>
                  <View style={styles.documentContent}>
                    <Text style={styles.documentName}>
                      {t("completion.docs.transportLicence")}
                    </Text>
                    <Text style={styles.documentSubtext}>
                      {verification?.documents?.transportLicenceSkipped
                        ? t("completion.docs.status.ignored")
                        : verification?.documents?.licenceTransport?.length > 0
                        ? t("completion.docs.status.filesCount").replace(
                            "{{count}}",
                            verification.documents.licenceTransport.length
                          )
                        : t("completion.docs.status.notProvided")}
                    </Text>
                  </View>
                  <View style={styles.documentStatus}>
                    <Text
                      style={[
                        styles.statusIcon,
                        verification?.documents?.transportLicenceSkipped ||
                        verification?.documents?.licenceTransport?.length === 0
                          ? styles.statusMissing
                          : styles.statusSuccess,
                      ]}
                    >
                      {verification?.documents?.transportLicenceSkipped ||
                      verification?.documents?.licenceTransport?.length === 0
                        ? "❌"
                        : "✅"}
                    </Text>
                  </View>
                </View>

                {/* Procuration - Only for DRIVER workflow */}
                {verification?.actorRole === "DRIVER" && (
                  <View style={styles.documentListItem}>
                    <View style={styles.documentIconContainer}>
                      <Text style={styles.documentIcon}>📝</Text>
                    </View>
                    <View style={styles.documentContent}>
                      <Text style={styles.documentName}>
                        {t("completion.docs.procuration")}
                      </Text>
                      <Text style={styles.documentSubtext}>
                        {verification?.owner?.skipProcuration
                          ? t("completion.docs.status.ignored")
                          : verification?.hasProcuration
                          ? t("completion.docs.status.provided")
                          : t("completion.docs.status.notProvidedFemale")}
                      </Text>
                    </View>
                    <View style={styles.documentStatus}>
                      <Text
                        style={[
                          styles.statusIcon,
                          verification?.owner?.skipProcuration ||
                          !verification?.hasProcuration
                            ? styles.statusMissing
                            : styles.statusSuccess,
                        ]}
                      >
                        {verification?.owner?.skipProcuration ||
                        !verification?.hasProcuration
                          ? "❌"
                          : "✅"}
                      </Text>
                    </View>
                  </View>
                )}
              </View>

              {/* Procuration blocking message for vehicle QR - only if needed */}
              {!!vehicleQrBlockedReason && (
                <View style={styles.blockBox}>
                  <Text style={styles.blockTitle}>
                    {t("completion.docs.blockTitle")}
                  </Text>
                  <Text style={styles.blockText}>{vehicleQrBlockedReason}</Text>
                </View>
              )}
            </View>
          </ScrollView>

          {/* Fixed Footer - stays at bottom */}
          <View style={styles.modalButtonsRow}>
            <Pressable
              style={({ pressed }) => [
                styles.modalSecondaryBtn,
                pressed && { opacity: 0.9 },
              ]}
              onPress={() => setMissingModalVisible(false)}
              disabled={saving}
            >
              <Text style={styles.modalSecondaryText}>
                {t("completion.docs.back")}
              </Text>
            </Pressable>

            <Pressable
              style={({ pressed }) => [
                styles.modalPrimaryBtn,
                pressed && { opacity: 0.9 },
                saving && { opacity: 0.7 },
              ]}
              onPress={proceedFinish}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.modalPrimaryText}>
                  {t("completion.docs.continue")}
                </Text>
              )}
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

return (
  <View style={styles.screen}>
    <Header />

    <MissingDocsModal />
    <SurveyModal 
        visible={surveyModalVisible}
        onClose={handleSurveyComplete}
    />

    {!completed ? (
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <View style={styles.header}>
          <Text style={styles.title}>{t("completion.requestedTitle")}</Text>

          <Text style={styles.subtitle}>
            {t("completion.requestedSubtitle_part1")}
            {" "}
          </Text>
        </View>

        <Pressable
          style={({ pressed }) => [
            styles.primaryButton,
            pressed && { opacity: 0.9, transform: [{ scale: 0.99 }] },
            saving && { opacity: 0.7 },
          ]}
          onPress={handleFinishPress}
          disabled={saving}
        >
          {saving ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.primaryButtonText}>
              {t("completion.finishVerification")}
            </Text>
          )}
        </Pressable>

        <View style={{ height: 22 }} />
      </ScrollView>
    ) : (
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <View style={styles.header}>
          <View style={styles.successBadge}>
            <Text style={styles.successBadgeText}>
              ✅ {t("completion.completedBadge")}
            </Text>
          </View>

          <Text style={styles.successTitle}>{t("completion.submittedTitle")}</Text>
          {/* <Text style={styles.successSubtitle}>{t("completion.submittedSubtitle")}</Text> */}
        </View>

       <View style={[styles.infoBox, { marginTop: 16 }]}> 
          <Text style={styles.infoTitle}>{t("completion.pendingTitle")}</Text>
          <Text style={styles.infoText}>
            {t("completion.pendingMessage")}
          </Text>
          <ActivityIndicator 
            size="large" 
            color="#0000ff" 
            style={{ marginTop: 16, alignSelf: 'center' }} 
          />
        </View>

          {/* <View style={styles.summarySection}>
            <Text style={styles.summaryTitle}>
              {t('completion.summaryTitle', 'Récapitulatif de votre demande')}
            </Text>
            <Text style={styles.summaryDescription}>
              {t('completion.summaryDescription', 'Téléchargez un document récapitulatif contenant toutes les informations que vous avez fournies, ainsi que le statut de vos documents.')}
            </Text>
            
            <Pressable
              style={({ pressed }) => [
                styles.summaryButton,
                pressed && { opacity: 0.9, transform: [{ scale: 0.99 }] },
                generatingPDF && styles.summaryButtonDisabled
              ]}
              onPress={handleDownloadSummary}
              disabled={generatingPDF}
            >
              {generatingPDF ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <>
                  <Text style={styles.summaryButtonIcon}>📋</Text>
                  <Text style={styles.summaryButtonText}>
                    {t('completion.downloadSummary', 'Télécharger le récapitulatif')}
                  </Text>
                </>
              )}
            </Pressable>
          </View> */}
            {/* New Survey Button */}
          <Pressable
            style={({ pressed }) => [
              styles.surveyButton,
              pressed && { opacity: 0.9, transform: [{ scale: 0.99 }] },
            ]}
            onPress={() => setSurveyModalVisible(true)}
          >
            <Text style={styles.surveyButtonIcon}>📝</Text>
            <Text style={styles.surveyButtonText}>
              {t('survey.takeSurvey')}
            </Text>
          </Pressable>
          
          {/* Start New Form Button */}
          <Pressable
            style={({ pressed }) => [
              styles.newFormButton,
              pressed && { opacity: 0.9, transform: [{ scale: 0.99 }] },
            ]}
            onPress={handleStartNew}
          >
            <Text style={styles.newFormButtonText}>
              {t("completion.startNewForm")}
            </Text>
          </Pressable>

        <View style={{ height: 22 }} />
      </ScrollView>
    )}
  </View>
);

}

// const SurveyModal = ({ visible, onClose }) => {
//   const { t } = useTranslation();
//   const [selectedEase, setSelectedEase] = useState(null);
//   const [selectedRecommend, setSelectedRecommend] = useState(null);
//   const [extraComments, setExtraComments] = useState('');
//   const [showThankYou, setShowThankYou] = useState(false);
//   const [isCommentFocused, setIsCommentFocused] = useState(false);

//   const easeOptions = [
//     { value: 'complicated', label: t('survey.ease.complicated') },
//     { value: 'not_very_easy', label: t('survey.ease.notVeryEasy') },
//     { value: 'easy', label: t('survey.ease.easy') },
//     { value: 'very_easy', label: t('survey.ease.veryEasy') },
//   ];

//   const recommendOptions = [
//     { value: 'not_likely', label: t('survey.recommend.notLikely') },
//     { value: 'somewhat_likely', label: t('survey.recommend.somewhatLikely') },
//     { value: 'likely', label: t('survey.recommend.likely') },
//     { value: 'very_likely', label: t('survey.recommend.veryLikely') },
//   ];

//   const handleClose = () => {
//     console.log('Survey response:', {
//       ease: selectedEase,
//       recommend: selectedRecommend,
//       comments: extraComments,
//       timestamp: new Date().toISOString()
//     });
    
//     setSelectedEase(null);
//     setSelectedRecommend(null);
//     setExtraComments('');
//     setShowThankYou(false);
//     onClose();
//   };

//   const handleSkip = () => {
//     handleClose();
//   };

//   const handleSubmit = () => {
//     if (!selectedEase) {
//       Alert.alert(t('common.error'), t('survey.requiredEase'));
//       return;
//     }
//     // Show thank you screen instead of closing immediately
//     setShowThankYou(true);
//   };

//   const handleThankYouClose = () => {
//     handleClose();
//   };

//   // Thank You Screen
//   if (showThankYou) {
//     return (
//       <Modal
//         visible={visible}
//         transparent
//         animationType="fade"
//         onRequestClose={handleThankYouClose}
//       >
//         <View style={styles.modalOverlay}>
//           <View style={[styles.modalCard, styles.thankYouCard]}>
//             {/* Success Icon with gradient-like effect using your colors */}
//             <View style={styles.thankYouIconContainer}>
//               <View style={[styles.thankYouCircle, { backgroundColor: COLORS.successLight }]}>
//                 <View style={[styles.thankYouInnerCircle, { backgroundColor: COLORS.success }]}>
//                   <Text style={styles.thankYouIcon}>✓</Text>
//                 </View>
//               </View>
//             </View>

//             {/* Thank You Text */}
//             <Text style={styles.thankYouTitle}>{t('survey.thankYou.title')}</Text>
//             <Text style={styles.thankYouMessage}>
//               {t('survey.thankYou.message')}
//             </Text>

//             {/* Decorative accent line */}
//             <View style={[styles.thankYouAccent, { backgroundColor: COLORS.primary }]} />

//             {/* Close Button */}
//             <Pressable
//               style={({ pressed }) => [
//                 styles.thankYouButton,
//                 pressed && styles.buttonPressed,
//                 { backgroundColor: COLORS.primary }
//               ]}
//               onPress={handleThankYouClose}
//             >
//               <Text style={styles.thankYouButtonText}>
//                 {t('survey.thankYou.continue')}
//               </Text>
//             </Pressable>
//           </View>
//         </View>
//       </Modal>
//     );
//   }

//   // Survey Form Screen
//   return (
//     <Modal
//       visible={visible}
//       transparent
//       animationType="fade"
//       onRequestClose={handleSkip}
//     >
//       <View style={styles.modalOverlay}>
//         <View style={[styles.modalCard, styles.surveyCard]}>
//           {/* Header with accent color */}
//           <View style={styles.surveyHeader}>
//             <View style={styles.titleContainer}>
//               <View style={[styles.titleAccent, { backgroundColor: COLORS.primary }]} />
//               <Text style={styles.surveyTitle}>{t('survey.title')}</Text>
//             </View>
//             <Pressable 
//               onPress={handleSkip}
//               style={styles.closeButton}
//               hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
//             >
//               <Text style={styles.closeButtonText}>✕</Text>
//             </Pressable>
//           </View>

//           <Text style={styles.surveySubtitle}>{t('survey.subtitle')}</Text>

//           <ScrollView 
//             style={styles.surveyScrollView}
//             showsVerticalScrollIndicator={false}
//           >
//             {/* Ease of use question */}
//             <View style={styles.surveySection}>
//               <View style={styles.questionHeader}>
//                 <View style={[styles.questionDot, { backgroundColor: COLORS.primary }]} />
//                 <Text style={styles.surveyQuestion}>
//                   {t('survey.ease.question')}
//                 </Text>
//                 <Text style={styles.requiredBadge}>*</Text>
//               </View>
//               <View style={styles.optionsRow}>
//                 {easeOptions.map((option) => (
//                   <Pressable
//                     key={option.value}
//                     style={[
//                       styles.optionPill,
//                       selectedEase === option.value && { 
//                         backgroundColor: COLORS.primary,
//                         borderColor: COLORS.primary 
//                       }
//                     ]}
//                     onPress={() => setSelectedEase(option.value)}
//                   >
//                     <Text style={[
//                       styles.optionPillText,
//                       selectedEase === option.value && styles.optionPillTextSelected
//                     ]}>
//                       {option.label}
//                     </Text>
//                   </Pressable>
//                 ))}
//               </View>
//             </View>

//             {/* Recommendation question */}
//             <View style={styles.surveySection}>
//               <View style={styles.questionHeader}>
//                 <View style={[styles.questionDot, { backgroundColor: COLORS.secondary }]} />
//                 <Text style={styles.surveyQuestion}>
//                   {t('survey.recommend.question')}
//                 </Text>
//               </View>
//               <View style={styles.optionsRow}>
//                 {recommendOptions.map((option) => (
//                   <Pressable
//                     key={option.value}
//                     style={[
//                       styles.optionPill,
//                       selectedRecommend === option.value && { 
//                         backgroundColor: COLORS.secondary,
//                         borderColor: COLORS.secondary 
//                       }
//                     ]}
//                     onPress={() => setSelectedRecommend(option.value)}
//                   >
//                     <Text style={[
//                       styles.optionPillText,
//                       selectedRecommend === option.value && styles.optionPillTextSelected
//                     ]}>
//                       {option.label}
//                     </Text>
//                   </Pressable>
//                 ))}
//               </View>
//             </View>

//             {/* Extra comments */}
//             <View style={styles.surveySection}>
//               <View style={styles.questionHeader}>
//                 <View style={[styles.questionDot, { backgroundColor: COLORS.tertiary }]} />
//                 <Text style={styles.surveyQuestion}>
//                   {t('survey.comments.label')}
//                 </Text>
//               </View>
//               <View style={styles.commentContainer}>
//                 <TextInput
//                   style={[
//                     styles.commentInput,
//                     isCommentFocused && { 
//                       borderColor: COLORS.primary,
//                       borderWidth: 2,
//                       backgroundColor: '#FFFFFF'
//                     }
//                   ]}
//                   placeholder={t('survey.comments.placeholder')}
//                   placeholderTextColor={COLORS.textMuted}
//                   value={extraComments}
//                   onChangeText={setExtraComments}
//                   multiline
//                   numberOfLines={3}
//                   textAlignVertical="top"
//                   onFocus={() => setIsCommentFocused(true)}
//                   onBlur={() => setIsCommentFocused(false)}
//                 />
//               </View>
//             </View>

//             <Text style={styles.surveyFootnote}>
//               {t('survey.note')}
//             </Text>
//           </ScrollView>

//           {/* Action Buttons */}
//           <View style={styles.surveyFooter}>
//             <Pressable
//               style={({ pressed }) => [
//                 styles.surveySecondaryButton,
//                 pressed && styles.buttonPressed
//               ]}
//               onPress={handleSkip}
//             >
//               <Text style={styles.surveySecondaryText}>{t('survey.skip')}</Text>
//             </Pressable>

//             <Pressable
//               style={({ pressed }) => [
//                 styles.surveyPrimaryButton,
//                 pressed && styles.buttonPressed,
//                 !selectedEase && styles.surveyPrimaryButtonDisabled
//               ]}
//               onPress={handleSubmit}
//               disabled={!selectedEase}
//             >
//               <Text style={styles.surveyPrimaryText}>{t('survey.submit')}</Text>
//             </Pressable>
//           </View>
//         </View>
//       </View>
//     </Modal>
//   );
// };

// Update the handleSubmit and handleSkip functions in SurveyModal

const SurveyModal = ({ visible, onClose }) => {
  const { t } = useTranslation();
  const { verification } = useContext(AppContext);
  const [selectedEase, setSelectedEase] = useState(null);
  const [selectedRecommend, setSelectedRecommend] = useState(null);
  const [extraComments, setExtraComments] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);
  const [isCommentFocused, setIsCommentFocused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);

  const easeOptions = [
    { value: 'complicated', label: t('survey.ease.complicated') },
    { value: 'not_very_easy', label: t('survey.ease.notVeryEasy') },
    { value: 'easy', label: t('survey.ease.easy') },
    { value: 'very_easy', label: t('survey.ease.veryEasy') },
  ];

  const recommendOptions = [
    { value: 'not_likely', label: t('survey.recommend.notLikely') },
    { value: 'somewhat_likely', label: t('survey.recommend.somewhatLikely') },
    { value: 'likely', label: t('survey.recommend.likely') },
    { value: 'very_likely', label: t('survey.recommend.veryLikely') },
  ];

  const submitFeedback = async () => {
    if (!verification?.verificationId) {
      console.error('No verification ID found');
      setSubmissionError('ID de vérification manquant');
      return false;
    }

    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      const response = await fetch(
        `https://api.taxi-yaounde.com/api/verification/${verification.verificationId}/feedback`,
        // `http://localhost:4000/api/verification/${verification.verificationId}/feedback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ease: selectedEase,
            recommend: selectedRecommend,
            comments: extraComments.trim() || null,
            actorRole: verification.actorRole
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit feedback');
      }

      console.log('Feedback submitted successfully:', data);
      return true;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmissionError(error.message || 'Erreur lors de l\'envoi du feedback');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedEase(null);
    setSelectedRecommend(null);
    setExtraComments('');
    setShowThankYou(false);
    setSubmissionError(null);
    setIsSubmitting(false);
    onClose();
  };

  const handleSkip = async () => {
    // Optionally track skips? For now just close
    handleClose();
  };

  const handleSubmit = async () => {
    if (!selectedEase) {
      Alert.alert(t('common.error'), t('survey.requiredEase'));
      return;
    }

    const success = await submitFeedback();
    
    if (success) {
      setShowThankYou(true);
    } else {
      Alert.alert(
        t('common.error'), 
        submissionError || t('survey.submitError', "Une erreur est survenue lors de l'envoi")
      );
    }
  };

  const handleThankYouClose = () => {
    handleClose();
  };

  // Thank You Screen
  if (showThankYou) {
    return (
      <Modal
        visible={visible}
        transparent
        animationType="fade"
        onRequestClose={handleThankYouClose}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalCard, styles.thankYouCard]}>
            {/* Success Icon */}
            <View style={styles.thankYouIconContainer}>
              <View style={[styles.thankYouCircle, { backgroundColor: COLORS.successLight }]}>
                <View style={[styles.thankYouInnerCircle, { backgroundColor: COLORS.success }]}>
                  <Text style={styles.thankYouIcon}>✓</Text>
                </View>
              </View>
            </View>

            {/* Thank You Text */}
            <Text style={styles.thankYouTitle}>{t('survey.thankYou.title')}</Text>
            <Text style={styles.thankYouMessage}>
              {t('survey.thankYou.message')}
            </Text>

            {/* Decorative accent line */}
            <View style={[styles.thankYouAccent, { backgroundColor: COLORS.primary }]} />

            {/* Close Button */}
            <Pressable
              style={({ pressed }) => [
                styles.thankYouButton,
                pressed && styles.buttonPressed,
                { backgroundColor: COLORS.primary }
              ]}
              onPress={handleThankYouClose}
            >
              <Text style={styles.thankYouButtonText}>
                {t('survey.thankYou.continue')}
              </Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    );
  }

  // Survey Form Screen
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleSkip}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.modalCard, styles.surveyCard]}>
          {/* Header */}
          <View style={styles.surveyHeader}>
            <View style={styles.titleContainer}>
              <View style={[styles.titleAccent, { backgroundColor: COLORS.primary }]} />
              <Text style={styles.surveyTitle}>{t('survey.title')}</Text>
            </View>
            <Pressable 
              onPress={handleSkip}
              style={styles.closeButton}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Text style={styles.closeButtonText}>✕</Text>
            </Pressable>
          </View>

          <Text style={styles.surveySubtitle}>
            {t('survey.subtitle')} 
            {verification?.verificationId && (
              <Text style={styles.subtitleBold}> (ID: {verification.verificationId})</Text>
            )}
          </Text>

          <ScrollView 
            style={styles.surveyScrollView}
            showsVerticalScrollIndicator={false}
          >
            {/* Ease of use question */}
            <View style={styles.surveySection}>
              <View style={styles.questionHeader}>
                <View style={[styles.questionDot, { backgroundColor: COLORS.primary }]} />
                <Text style={styles.surveyQuestion}>
                  {t('survey.ease.question')}
                </Text>
                <Text style={styles.requiredBadge}>*</Text>
              </View>
              <View style={styles.optionsRow}>
                {easeOptions.map((option) => (
                  <Pressable
                    key={option.value}
                    style={[
                      styles.optionPill,
                      selectedEase === option.value && { 
                        backgroundColor: COLORS.primary,
                        borderColor: COLORS.primary 
                      }
                    ]}
                    onPress={() => setSelectedEase(option.value)}
                  >
                    <Text style={[
                      styles.optionPillText,
                      selectedEase === option.value && styles.optionPillTextSelected
                    ]}>
                      {option.label}
                    </Text>
                  </Pressable>
                ))}
              </View>
            </View>

            {/* Recommendation question */}
            <View style={styles.surveySection}>
              <View style={styles.questionHeader}>
                <View style={[styles.questionDot, { backgroundColor: COLORS.secondary }]} />
                <Text style={styles.surveyQuestion}>
                  {t('survey.recommend.question')}
                </Text>
              </View>
              <View style={styles.optionsRow}>
                {recommendOptions.map((option) => (
                  <Pressable
                    key={option.value}
                    style={[
                      styles.optionPill,
                      selectedRecommend === option.value && { 
                        backgroundColor: COLORS.secondary,
                        borderColor: COLORS.secondary 
                      }
                    ]}
                    onPress={() => setSelectedRecommend(option.value)}
                  >
                    <Text style={[
                      styles.optionPillText,
                      selectedRecommend === option.value && styles.optionPillTextSelected
                    ]}>
                      {option.label}
                    </Text>
                  </Pressable>
                ))}
              </View>
            </View>

            {/* Extra comments */}
            <View style={styles.surveySection}>
              <View style={styles.questionHeader}>
                <View style={[styles.questionDot, { backgroundColor: COLORS.tertiary }]} />
                <Text style={styles.surveyQuestion}>
                  {t('survey.comments.label')}
                </Text>
              </View>
              <View style={styles.commentContainer}>
                <TextInput
                  style={[
                    styles.commentInput,
                    isCommentFocused && styles.commentInputFocused
                  ]}
                  placeholder={t('survey.comments.placeholder')}
                  placeholderTextColor={COLORS.textMuted}
                  value={extraComments}
                  onChangeText={setExtraComments}
                  multiline
                  numberOfLines={3}
                  textAlignVertical="top"
                  onFocus={() => setIsCommentFocused(true)}
                  onBlur={() => setIsCommentFocused(false)}
                  editable={!isSubmitting}
                />
              </View>
            </View>

            {/* Error message if any */}
            {submissionError && (
              <View style={[styles.blockBox, { marginTop: 8 }]}>
                <Text style={styles.blockText}>{submissionError}</Text>
              </View>
            )}

            <Text style={styles.surveyFootnote}>
              {t('survey.note')}
            </Text>
          </ScrollView>

          {/* Action Buttons */}
          <View style={styles.surveyFooter}>
            <Pressable
              style={({ pressed }) => [
                styles.surveySecondaryButton,
                pressed && styles.buttonPressed,
                isSubmitting && { opacity: 0.5 }
              ]}
              onPress={handleSkip}
              disabled={isSubmitting}
            >
              <Text style={styles.surveySecondaryText}>{t('survey.skip')}</Text>
            </Pressable>

            <Pressable
              style={({ pressed }) => [
                styles.surveyPrimaryButton,
                pressed && styles.buttonPressed,
                (!selectedEase || isSubmitting) && styles.surveyPrimaryButtonDisabled
              ]}
              onPress={handleSubmit}
              disabled={!selectedEase || isSubmitting}
            >
              {isSubmitting ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <Text style={styles.surveyPrimaryText}>{t('survey.submit')}</Text>
              )}
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: COLORS.background },
  scroll: { paddingHorizontal: 16, paddingBottom: 40, flexGrow: 1, justifyContent: "center" },

  scrollView: {
      flex: 1,
      width: '100%',
    },
    scrollViewContent: {
      paddingBottom: 16,
    },

  header: { alignItems: "center", marginBottom: 30 },
 
  successBadge: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 999,
    marginBottom: 10,
    backgroundColor: "rgba(22, 163, 74, 0.12)",
    borderWidth: 1,
    borderColor: "rgba(22, 163, 74, 0.25)",
  },
  badgeText: { fontSize: 12, fontWeight: "700", color: COLORS.textDark },
  successBadgeText: { fontSize: 14, fontWeight: "800", color: COLORS.success },

  title: {
    fontSize: 24,
    fontWeight: "800",
    color: COLORS.textDark,
    textAlign: "center",
    marginBottom: 12,
  },
  successTitle: {
    fontSize: 28,
    fontWeight: "900",
    color: COLORS.textDark,
    textAlign: "center",
    marginBottom: 12,
  },
  subtitle: { fontSize: 14, color: COLORS.textMuted, textAlign: "center", lineHeight: 20, maxWidth: 500 },
  successSubtitle: { fontSize: 15, color: COLORS.textMuted, textAlign: "center", lineHeight: 22, maxWidth: 500 },

  primaryButton: {
    marginTop: 20,
    backgroundColor: COLORS.primary,
    paddingVertical: 16,
    borderRadius: 14,
    alignItems: "center",
    shadowColor: COLORS.primary,
    shadowOpacity: 0.2,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 4,
  },
  primaryButtonText: { color: "#FFFFFF", fontSize: 16, fontWeight: "800" },

  qrContainer: {
    alignItems: "center",
    marginVertical: 30,
    padding: 20,
    backgroundColor: COLORS.card,
    borderRadius: 16,
    shadowColor: "#000",
    shadowOpacity: 0.05,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  qrTitle: { fontSize: 18, fontWeight: "700", color: COLORS.textDark, textAlign: "center", marginBottom: 8 },
  qrDescription: { fontSize: 14, color: COLORS.textMuted, textAlign: "center", marginBottom: 20, lineHeight: 20 },

  qrImageContainer: {
    backgroundColor: "#FFFFFF",
    padding: 15,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: COLORS.border,
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 3,
  },
  qrImage: { width: 200, height: 200 },

  successIconContainer: { alignItems: "center" },
  successCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: "rgba(22, 163, 74, 0.12)",
    borderWidth: 2,
    borderColor: "rgba(22, 163, 74, 0.25)",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  checkIcon: { color: COLORS.success, fontSize: 36, fontWeight: "900" },
  successMessage: { fontSize: 16, color: COLORS.textDark, textAlign: "center", fontWeight: "600", lineHeight: 22, maxWidth: 400 },

  downloadButton: {
    marginTop: 15,
    backgroundColor: COLORS.primary,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
    shadowColor: COLORS.primary,
    shadowOpacity: 0.2,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 3 },
    elevation: 3,
  },
  downloadButtonText: { color: "#FFFFFF", fontSize: 15, fontWeight: "700", marginLeft: 5 },
  
  downloadButtonSmall: {
    marginTop: 12,
    backgroundColor: COLORS.primary,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
  },
  downloadButtonSmallText: {
    color: "#FFFFFF",
    fontSize: 13,
    fontWeight: "600",
    marginLeft: 5,
  },

  newFormButton: {
    marginTop: 30,
    backgroundColor: COLORS.secondary,
    paddingVertical: 16,
    borderRadius: 14,
    alignItems: "center",
    shadowColor: COLORS.secondary,
    shadowOpacity: 0.2,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 4,
  },
  newFormButtonText: { color: "#FFFFFF", fontSize: 16, fontWeight: "800" },

  // Enhanced Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(17, 24, 39, 0.55)",
    padding: 16,
    justifyContent: "center",
    zIndex: 9999,
  },
  modalCard: {
    backgroundColor: "#FFF",
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: COLORS.border,
    maxHeight: '80%',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "900",
    color: COLORS.textDark,
    marginBottom: 16,
    textAlign: 'center',
  },
  
  subtitleBold: {
  fontWeight: "600",
},

  // Summary styles
  summaryContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    backgroundColor: COLORS.background,
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  summaryItem: {
    alignItems: 'center',
  },
  summaryNumber: {
    fontSize: 24,
    fontWeight: '900',
    color: COLORS.success,
    marginBottom: 4,
  },
  summaryLabel: {
    fontSize: 12,
    color: COLORS.textMuted,
    fontWeight: '600',
  },
  summaryDivider: {
    width: 1,
    height: 30,
    backgroundColor: COLORS.border,
  },

  // Section styles
  sectionContainer: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "800",
    color: COLORS.textDark,
    marginBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    paddingBottom: 8,
  },

  // Document row styles
  documentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
  },
  documentIcon: {
    fontSize: 20,
    marginRight: 12,
    width: 30,
    textAlign: 'center',
  },
  documentInfo: {
    flex: 1,
  },
  documentLabel: {
    fontSize: 14,
    fontWeight: "700",
    color: COLORS.textDark,
    marginBottom: 2,
  },
  documentStatusSuccess: {
    fontSize: 12,
    color: COLORS.success,
    fontWeight: '600',
  },
  documentStatusWarning: {
    fontSize: 12,
    color: COLORS.warning,
    fontWeight: '600',
  },
  documentStatusError: {
    fontSize: 12,
    color: COLORS.danger,
    fontWeight: '600',
  },

  modalText: {
    fontSize: 13,
    color: COLORS.textMuted,
    lineHeight: 18,
  },
  missingRow: { flexDirection: "row", alignItems: "flex-start", marginTop: 6 },
  missingDot: { width: 18, fontSize: 16, color: COLORS.textDark, lineHeight: 18 },
  missingText: { flex: 1, fontSize: 14, color: COLORS.textDark, lineHeight: 20 },

  blockBox: {
    marginTop: 14,
    padding: 12,
    borderRadius: 12,
    backgroundColor: COLORS.dangerLight,
    borderWidth: 1,
    borderColor: "rgba(220, 38, 38, 0.20)",
  },
  blockTitle: { fontSize: 14, fontWeight: "900", color: COLORS.danger, marginBottom: 6 },
  blockText: { fontSize: 13, color: COLORS.textDark, lineHeight: 18 },

  infoBox: {
    marginTop: 14,
    padding: 16,
    borderRadius: 12,
    backgroundColor: "rgba(59, 130, 246, 0.08)",
    borderWidth: 1,
    borderColor: "rgba(59, 130, 246, 0.20)",
    marginHorizontal: 16,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: COLORS.textDark,
    marginBottom: 8,
  },
  infoText: {
    fontSize: 14,
    color: COLORS.textMuted,
    lineHeight: 20,
  },

  modalButtonsRow: { flexDirection: "row", gap: 10, marginTop: 20 },
  modalSecondaryBtn: {
    flex: 1,
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: "center",
    backgroundColor: "#F3F4F6",
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  modalSecondaryText: { fontWeight: "800", color: COLORS.textDark },

  modalPrimaryBtn: {
    flex: 1,
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: "center",
    backgroundColor: COLORS.primary,
  },
  modalPrimaryText: { fontWeight: "900", color: "#FFF" },
  // Add to your styles object

documentListContainer: {
  width: '100%',
},

documentList: {
  backgroundColor: COLORS.background,
  borderRadius: 12,
  marginVertical: 8,
  overflow: 'hidden',
},

documentListItem: {
  flexDirection: 'row',
  alignItems: 'center',
  paddingVertical: 12,
  paddingHorizontal: 16,
  borderBottomWidth: 1,
  borderBottomColor: COLORS.border,
  backgroundColor: COLORS.card,
},

documentIconContainer: {
  width: 40,
  alignItems: 'center',
  justifyContent: 'center',
},

documentIcon: {
  fontSize: 20,
},

documentContent: {
  flex: 1,
  marginLeft: 8,
},

documentName: {
  fontSize: 15,
  fontWeight: '600',
  color: COLORS.textDark,
  marginBottom: 2,
},

documentSubtext: {
  fontSize: 12,
  color: COLORS.textMuted,
},

documentStatus: {
  width: 40,
  alignItems: 'center',
  justifyContent: 'center',
},

statusIcon: {
  fontSize: 20,
},

statusSuccess: {
  color: COLORS.success,
},

statusMissing: {
  color: COLORS.danger,
},

// Replace with these colorful but professional styles
surveyCard: {
  maxHeight: '85%',
  paddingHorizontal: 24,
  paddingVertical: 24,
},

surveyHeader: {
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: 16,
},

titleContainer: {
  flexDirection: 'row',
  alignItems: 'center',
  flex: 1,
},

titleAccent: {
  width: 4,
  height: 24,
  backgroundColor: COLORS.primary,
  borderRadius: 2,
  marginRight: 12,
},

surveyTitle: {
  fontSize: 20,
  fontWeight: '600',
  color: COLORS.textDark,
  letterSpacing: -0.3,
},

closeButton: {
  padding: 4,
  borderRadius: 16,
  width: 32,
  height: 32,
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: COLORS.background,
},

closeButtonText: {
  fontSize: 18,
  fontWeight: '400',
  color: COLORS.textMuted,
},

surveySubtitle: {
  fontSize: 14,
  color: COLORS.textMuted,
  marginBottom: 24,
  lineHeight: 20,
  paddingLeft: 16,
  borderLeftWidth: 1,
  borderLeftColor: COLORS.border,
  fontStyle: 'italic',
},

surveyScrollView: {
  flex: 1,
  marginBottom: 20,
},

surveySection: {
  marginBottom: 28,
  backgroundColor: COLORS.card,
  borderRadius: 12,
  padding: 16,
  borderWidth: 1,
  borderColor: COLORS.border,
  shadowColor: COLORS.textDark,
  shadowOpacity: 0.02,
  shadowRadius: 4,
  shadowOffset: { width: 0, height: 2 },
  elevation: 1,
},

questionHeader: {
  flexDirection: 'row',
  alignItems: 'center',
  marginBottom: 16,
},

questionDot: {
  width: 6,
  height: 6,
  borderRadius: 3,
  marginRight: 8,
},

surveyQuestion: {
  fontSize: 15,
  fontWeight: '500',
  color: COLORS.textDark,
  lineHeight: 22,
  flex: 1,
},

requiredBadge: {
  fontSize: 14,
  fontWeight: '600',
  color: COLORS.danger,
  marginLeft: 4,
},

optionsRow: {
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 8,
  marginTop: 4,
},

optionPill: {
  paddingVertical: 8,
  paddingHorizontal: 16,
  borderRadius: 20,
  backgroundColor: COLORS.background,
  borderWidth: 1,
  borderColor: COLORS.border,
},

optionPillText: {
  fontSize: 14,
  fontWeight: '500',
  color: COLORS.textDark,
},

optionPillTextSelected: {
  color: '#FFFFFF',
  fontWeight: '600',
},

commentContainer: {
  marginTop: 4,
},

commentInput: {
  borderWidth: 1,
  borderColor: COLORS.border,
  borderRadius: 10,
  paddingHorizontal: 16,
  paddingVertical: 12,
  fontSize: 14,
  color: COLORS.textDark,
  backgroundColor: COLORS.background,
  minHeight: 100,
  textAlignVertical: 'top',
  lineHeight: 20,
},

commentInputFocused: {
  borderColor: COLORS.primary,
  borderWidth: 2,
  backgroundColor: '#FFFFFF',
},

surveyFootnote: {
  fontSize: 12,
  color: COLORS.textMuted,
  marginTop: 8,
  marginBottom: 4,
  lineHeight: 18,
  paddingHorizontal: 4,
  fontWeight: '400',
},

surveyFooter: {
  flexDirection: 'row',
  gap: 12,
  borderTopWidth: 1,
  borderTopColor: COLORS.border,
  paddingTop: 20,
  marginTop: 4,
},

surveySecondaryButton: {
  flex: 1,
  borderRadius: 10,
  paddingVertical: 12,
  alignItems: 'center',
  backgroundColor: '#FFFFFF',
  borderWidth: 1,
  borderColor: COLORS.border,
},

surveySecondaryText: {
  fontWeight: '500',
  fontSize: 15,
  color: COLORS.textDark,
},

surveyPrimaryButton: {
  flex: 1,
  borderRadius: 10,
  paddingVertical: 12,
  alignItems: 'center',
  backgroundColor: COLORS.primary,
  shadowColor: COLORS.primary,
  shadowOpacity: 0.15,
  shadowRadius: 6,
  shadowOffset: { width: 0, height: 3 },
  elevation: 3,
},

surveyPrimaryButtonDisabled: {
  backgroundColor: COLORS.border,
  shadowOpacity: 0,
  elevation: 0,
},

surveyPrimaryText: {
  fontWeight: '600',
  fontSize: 15,
  color: '#FFFFFF',
  letterSpacing: 0.3,
},

buttonPressed: {
  opacity: 0.9,
  transform: [{ scale: 0.98 }],
},
// Add these to your styles object
thankYouCard: {
  alignItems: 'center',
  paddingVertical: 32,
  paddingHorizontal: 24,
},

thankYouIconContainer: {
  marginBottom: 20,
},

thankYouCircle: {
  width: 100,
  height: 100,
  borderRadius: 50,
  justifyContent: 'center',
  alignItems: 'center',
},

thankYouInnerCircle: {
  width: 80,
  height: 80,
  borderRadius: 40,
  justifyContent: 'center',
  alignItems: 'center',
  shadowColor: COLORS.success,
  shadowOpacity: 0.3,
  shadowRadius: 8,
  shadowOffset: { width: 0, height: 4 },
  elevation: 6,
},

thankYouIcon: {
  fontSize: 40,
  color: '#FFFFFF',
  fontWeight: 'bold',
},

thankYouTitle: {
  fontSize: 24,
  fontWeight: '700',
  color: COLORS.textDark,
  marginBottom: 12,
  textAlign: 'center',
},

thankYouMessage: {
  fontSize: 16,
  color: COLORS.textMuted,
  textAlign: 'center',
  lineHeight: 24,
  marginBottom: 24,
  paddingHorizontal: 16,
},

thankYouAccent: {
  width: 60,
  height: 3,
  borderRadius: 1.5,
  marginBottom: 28,
},

thankYouButton: {
  paddingVertical: 14,
  paddingHorizontal: 32,
  borderRadius: 12,
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 200,
  shadowColor: COLORS.primary,
  shadowOpacity: 0.2,
  shadowRadius: 8,
  shadowOffset: { width: 0, height: 4 },
  elevation: 4,
},

thankYouButtonText: {
  fontSize: 16,
  fontWeight: '600',
  color: '#FFFFFF',
  letterSpacing: 0.5,
},
// Add these to your styles object
modalScrollView: {
  maxHeight: 400, // Adjust based on your needs
  width: '100%',
},

modalScrollContent: {
  paddingVertical: 8,
  paddingHorizontal: 4,
},

// Update modalCard to have a max height
modalCard: {
  backgroundColor: "#FFF",
  borderRadius: 16,
  padding: 20,
  borderWidth: 1,
  borderColor: COLORS.border,
  maxHeight: '80%', // Limit height to 80% of screen
  width: Platform.OS === 'web' ? '90%' : '95%',
  maxWidth: 500,
  alignSelf: 'center',
},

// Ensure the buttons stay at the bottom
modalButtonsRow: {
  flexDirection: "row",
  gap: 10,
  marginTop: 16,
  paddingTop: 12,
  borderTopWidth: 1,
  borderTopColor: COLORS.border,
},
// Add these to your styles object
summarySection: {
  marginTop: 30,
  marginBottom: 20,
  padding: 20,
  backgroundColor: COLORS.card,
  borderRadius: 16,
  borderWidth: 1,
  borderColor: COLORS.border,
  alignItems: 'center',
  shadowColor: "#000",
  shadowOpacity: 0.05,
  shadowRadius: 8,
  shadowOffset: { width: 0, height: 2 },
  elevation: 2,
},

summaryTitle: {
  fontSize: 18,
  fontWeight: '700',
  color: COLORS.textDark,
  marginBottom: 8,
  textAlign: 'center',
},

summaryDescription: {
  fontSize: 14,
  color: COLORS.textMuted,
  textAlign: 'center',
  marginBottom: 16,
  lineHeight: 20,
  paddingHorizontal: 10,
},

summaryButton: {
  backgroundColor: COLORS.secondary,
  paddingVertical: 14,
  paddingHorizontal: 24,
  borderRadius: 12,
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row',
  gap: 10,
  shadowColor: COLORS.secondary,
  shadowOpacity: 0.2,
  shadowRadius: 6,
  shadowOffset: { width: 0, height: 3 },
  elevation: 3,
  minWidth: 250,
},

summaryButtonDisabled: {
  opacity: 0.6,
},

summaryButtonIcon: {
  fontSize: 20,
  color: '#FFFFFF',
},

summaryButtonText: {
  color: '#FFFFFF',
  fontSize: 15,
  fontWeight: '700',
},
// Add these to your styles object
surveyButton: {
  marginTop: 16,
  backgroundColor: COLORS.teal || '#1CA7AE', // Use a different color than secondary
  paddingVertical: 14,
  paddingHorizontal: 24,
  borderRadius: 12,
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row',
  gap: 10,
  shadowColor: '#1CA7AE',
  shadowOpacity: 0.2,
  shadowRadius: 6,
  shadowOffset: { width: 0, height: 3 },
  elevation: 3,
  minWidth: 250,
  alignSelf: 'center',
},

surveyButtonIcon: {
  fontSize: 20,
  color: '#FFFFFF',
},

surveyButtonText: {
  color: '#FFFFFF',
  fontSize: 15,
  fontWeight: '600',
},
});
